
import GenieLogo from '@/components/ui/icon/icons/GenieLogo'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { CheckIcon } from 'lucide-react'
import { Typography } from '@/components/ui/typography'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'

type TWaitListState = 'initial' | 'success'

type Inputs = {
  name: string
  email: string
}

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  name: z.string(),
})

export function JoinWaitlist() {
  const [formState, setFormState] = useState<TWaitListState>('initial')

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<Inputs>({
    resolver: zodResolver(loginSchema),
  })

  const onSubmit = (_data: Inputs) => {
    setFormState('success')
  }

  return (
    <div className="md:bg-muted flex items-center justify-center bg-cover md:min-h-screen">
      <div className="flex h-screen w-full flex-col md:h-auto md:max-w-[440px] md:px-4 md:py-6">
        <Card className="rounded-none p-0 md:rounded-xl">
          {formState === 'initial' && (
            <CardHeader className="flex flex-col items-start border-b-0 p-6 pb-0 text-left">
              <Link to={'/'}>
                <GenieLogo className="mb-6 h-12 w-12" />
              </Link>
              <CardTitle className="text-lg font-bold">Join Waitlist</CardTitle>
              <CardDescription>
                Join the waitlist to get early access to Genie and be among the
                first to experience the future of creative workflows.
              </CardDescription>
            </CardHeader>
          )}

          <CardContent className={'p-6'}>
            {/* Join Waitlist Form */}
            {formState === 'success' && (
              <div className={'flex flex-col items-center space-y-6 py-18'}>
                <CheckIcon width={36} height={36} />
                <div className={'text-center'}>
                  <Typography variant={'h5'} className={'border-b-0'}>
                    Waitlist joined
                  </Typography>
                  <Typography className={'m-0! mt-2!'} variant={'muted'}>
                    We will send you updates
                  </Typography>
                </div>
                <Link to={'/'} className="w-full">
                  <Button size="lg" className="w-full">
                    Back to Landing page
                  </Button>
                </Link>
              </div>
            )}
            {formState === 'initial' && (
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className={'inline-block'}>
                      Name
                    </Label>
                    <Input
                      id="name"
                      placeholder="Your name"
                      type="text"
                      {...register('name')}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className={'inline-block'}>
                      Email
                    </Label>
                    <Input
                      id="email"
                      placeholder="Email"
                      type="email"
                      {...register('email')}
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-destructive">
                        {errors.email.message}
                      </p>
                    )}
                  </div>

                  <Button size="lg" className="w-full">
                    Join Waitlist
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
        {/* Footer: Copyright and Links */}
        <div className="bg-muted text-muted-foreground flex w-full flex-col gap-y-4 py-6 text-center text-sm md:flex-row md:justify-between md:bg-transparent">
          <div>© 2025 Genieart, inc</div>
          <div className="space-x-4">
            <a className="underline" href="#">
              Terms
            </a>
            <a className="underline" href="#">
              Privacy
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}
